import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  Monitor, 
  Megaphone, 
  Users, 
  ExternalLink,
  ChevronDown
} from 'lucide-react'

const DepartmentAccordion: React.FC = () => {
  const [activeKey, setActiveKey] = useState<string | null>(null)

  const departments = [
    {
      key: '0',
      title: 'Information Technology',
      icon: Monitor,
      color: 'bg-gray-600',
      services: [
        'VPN Access',
        'H-Dnet Support',
        'Password Resets',
        'Phone System Support',
        'Distribution List Changes',
        'Purchase Requests',
        'Software & Hardware Support',
        {
          name: 'Phishing Awareness Training',
          link: 'https://security.tmcassist.it'
        }
      ],
      ticketUrl: 'https://themotorcyclecompany.zendesk.com/hc/en-us/requests/new?ticket_form_id=14832461942029'
    },
    {
      key: '1',
      title: 'Marketing',
      icon: Megaphone,
      color: 'bg-gray-600',
      services: [
        'Tent Sales',
        'Text Blasts',
        'Door Flyers',
        'Social Media',
        'Employee Photos',
        'Lifecycle Campaign',
        'Motorcycle Inventory',
        'Dealership Website Issues',
        'Other Marketing & Promotions'
      ],
      ticketUrl: 'https://themotorcyclecompany.zendesk.com/hc/en-us/requests/new?ticket_form_id=37280583932045'
    },
    {
      key: '2',
      title: 'Human Resources',
      icon: Users,
      color: 'bg-gray-600',
      services: [
        'HR Questions',
        'Pay Plan Requests',
        'Employee Incidents',
        'KPA Information'
      ],
      ticketUrl: 'https://tmchr.zendesk.com/hc/en-us/requests/new'
    }
  ]

  const handleAccordionToggle = (key: string) => {
    setActiveKey(activeKey === key ? null : key)
  }

  return (
    <section className="py-8 sm:py-16">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 lg:items-start">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-left lg:sticky lg:top-[calc(5vh+7.25rem)]"
          >
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gradient mb-4">
              Department Support
            </h2>
            <p className="text-lg sm:text-xl text-gray-300 max-w-md font-futura-book">
              Get help from our specialized departments. Click on any department to see available services.
            </p>
            <div className="mt-6 pt-6 border-t border-white/10 max-w-md">
              <h3 className="text-lg font-semibold text-white mb-3">Support Hours</h3>
              <div className="space-y-2 text-sm text-gray-400">
                <p>IT Support: Mon-Sat 8AM-6PM</p>
                <p>HR Support: Mon-Fri 8AM-6PM</p>
                <p>Marketing: Mon-Fri 8AM-6PM</p>
              </div>
            </div>
          </motion.div>

          <div className="space-y-4">
        {departments.map((dept, index) => (
          <motion.div
            key={dept.key}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1, duration: 0.6 }}
            viewport={{ once: true }}
          >
            <div className="glass-effect border-0 overflow-hidden rounded-2xl">
              <button
                onClick={() => handleAccordionToggle(dept.key)}
                className="w-full text-left p-4 sm:p-6 hover:no-underline focus:no-underline focus:shadow-none bg-transparent border-0 touch-manipulation min-h-[60px] sm:min-h-[80px]"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 sm:gap-4">
                    <div className={`p-2 sm:p-3 rounded-xl ${dept.color} flex-shrink-0`}>
                      <dept.icon size={20} className="text-white sm:w-6 sm:h-6" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3 className="text-lg sm:text-xl font-semibold text-white mb-1">
                        {dept.title}
                      </h3>
                      <p className="text-gray-300 text-sm">
                        {dept.services.length} services available
                      </p>
                    </div>
                  </div>
                  <motion.div
                    animate={{ rotate: activeKey === dept.key ? 180 : 0 }}
                    transition={{ duration: 0.2, ease: "easeOut" }}
                    className="flex-shrink-0"
                  >
                    <ChevronDown size={20} className="text-white sm:w-6 sm:h-6" />
                  </motion.div>
                </div>
              </button>

              <AnimatePresence mode="wait">
                {activeKey === dept.key && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ 
                      duration: 0.2,
                      ease: "easeOut"
                    }}
                    className="p-4 sm:p-6 border-t border-white/10"
                  >
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                      <div>
                        <h4 className="text-lg font-semibold text-white mb-3">
                          Available Services
                        </h4>
                        <ul className="space-y-2">
                          {dept.services.map((service, serviceIndex) => (
                            <motion.li
                              key={serviceIndex}
                              initial={{ opacity: 0, x: -10 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ 
                                delay: serviceIndex * 0.02,
                                duration: 0.15,
                                ease: "easeOut"
                              }}
                              className="flex items-center gap-2 text-gray-300"
                            >
                              <div className="w-2 h-2 bg-white rounded-full flex-shrink-0" />
                              {typeof service === 'string' ? (
                                <span className="text-sm sm:text-base">{service}</span>
                              ) : (
                                <a
                                  href={service.link}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-white hover:text-gray-300 transition-colors duration-200 flex items-center gap-1 text-sm sm:text-base touch-manipulation py-1"
                                >
                                  {service.name}
                                  <ExternalLink size={14} />
                                </a>
                              )}
                            </motion.li>
                          ))}
                        </ul>
                      </div>
                      
                      <div className="flex flex-col justify-center">
                        <div className="text-center">
                          <h4 className="text-lg font-semibold text-white mb-3">
                            Need Help?
                          </h4>
                          <p className="text-gray-300 mb-4 font-futura-book text-sm sm:text-base">
                            Submit a ticket to get assistance from our {dept.title} team.
                          </p>
                          <motion.a
                            href={dept.ticketUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn-primary inline-flex items-center gap-2 touch-manipulation min-h-[44px] justify-center"
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                          >
                            Submit Ticket
                            <ExternalLink size={16} />
                          </motion.a>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        ))}
          </div>
        </div>
      </div>
    </section>
  )
}

export default DepartmentAccordion
